import React, { useState, useEffect } from 'react';
import { Search, Download, CheckCircle, XCircle } from 'lucide-react';
import { Table, Button, Form, InputGroup, Badge, Row, Col, Card } from 'react-bootstrap';
import './AdminOrders.css';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        fetch('/orders.xml?' + Date.now())
            .then(res => res.text())
            .then(xmlText => {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
                const orderNodes = xmlDoc.getElementsByTagName('Order');
                const xmlOrders = [];
                for (let i = 0; i < orderNodes.length; i++) {
                    const node = orderNodes[i];
                    const getVal = (tag) => node.getElementsByTagName(tag)[0]?.textContent || '';
                    const items = node.getElementsByTagName('item');
                    const parsedItems = [];
                    for (let j = 0; j < items.length; j++) {
                        parsedItems.push({
                            name: items[j].getElementsByTagName('name')[0]?.textContent || '',
                            qty: parseInt(items[j].getElementsByTagName('quantity')[0]?.textContent) || 1,
                            price: `₹${items[j].getElementsByTagName('price')[0]?.textContent || '0'}`,
                            weight: items[j].getElementsByTagName('weight')[0]?.textContent || '-',
                            message: items[j].getElementsByTagName('message')[0]?.textContent || ''
                        });
                    }
                    const paymentEl = node.getElementsByTagName('payment')[0];
                    const getPayVal = (tag) => paymentEl?.getElementsByTagName(tag)[0]?.textContent || '';
                    const addrEl = node.getElementsByTagName('deliveryAddress')[0];
                    const getAddrVal = (tag) => addrEl?.getElementsByTagName(tag)[0]?.textContent || '';
                    const paymentId = getPayVal('razorpayPaymentId');
                    const amount = getPayVal('amount');
                    xmlOrders.push({
                        id: `#ORD-${getVal('id').slice(-6)}`,
                        customer: getVal('userName'),
                        email: getVal('userEmail'),
                        phone: getAddrVal('phone'),
                        date: new Date(getVal('orderDate')).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
                        total: `₹${amount}`,
                        status: 'Pending',
                        address: {
                            name: getAddrVal('name'),
                            phone: getAddrVal('phone'),
                            fullAddress: getAddrVal('fullAddress'),
                            pincode: getAddrVal('pincode'),
                            nearby: getAddrVal('nearby')
                        },
                        payment: {
                            status: amount > 0 ? 'Paid' : 'Unpaid',
                            id: paymentId,
                            method: 'Razorpay',
                            amount: `₹${amount}`
                        },
                        items: parsedItems
                    });
                }
                if (xmlOrders.length > 0) {
                    setOrders(xmlOrders.reverse());
                }
            })
            .catch(() => {});
    }, []);

    const statusOptions = ['Pending', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled'];

    const handleStatusChange = (orderId, newStatus) => {
        setOrders(prev => prev.map(o =>
            o.id === orderId ? { ...o, status: newStatus } : o
        ));
        fetch('/api/order/update-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderId, status: newStatus })
        }).catch(e => console.error('Status update failed:', e));
    };

    return (
        <div className="admin-orders-page container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3 mt-3">
                <div className="admin-page-title">
                    <h1 className="h3 mb-0 fw-bold">Order Management</h1>
                    <p className="text-muted mb-0">Track, update and manage customer orders.</p>
                </div>
                <Button variant="primary" className="btn-bakery shadow-sm d-flex align-items-center gap-2 rounded-pill px-4">
                    <Download size={18} />
                    <span>Export Orders</span>
                </Button>
            </div>

            <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="p-2">
                    <Row className="g-2 align-items-center">
                        <Col lg={4} md={6}>
                            <InputGroup className="bg-light border rounded-pill px-2">
                                <InputGroup.Text className="bg-transparent border-0 text-muted">
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    className="bg-transparent border-0 shadow-none ps-0 x-small"
                                    placeholder="Search by Order ID or Customer..."
                                />
                            </InputGroup>
                        </Col>
                        <Col lg={8} md={6}>
                            <div className="d-flex justify-content-md-end gap-2 flex-wrap">
                                <Form.Select className="w-auto border rounded-pill px-3 shadow-none x-small">
                                    <option>Filter by Status</option>
                                    <option>Pending</option>
                                    <option>Confirmed</option>
                                    <option>Shipped</option>
                                    <option>Delivered</option>
                                    <option>Cancelled</option>
                                </Form.Select>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm overflow-hidden">
                <Card.Body className="p-0">
                    <Table responsive hover className="admin-bootstrap-table mb-0 align-middle">
                        <thead>
                            <tr className="bg-light x-small text-uppercase text-muted fw-bold">
                                <th className="border-0 ps-4 py-2" style={{ width: '50px' }}>#</th>
                                <th className="border-0 py-2">Order</th>
                                <th className="border-0 py-2">Customer</th>
                                <th className="border-0 py-2">Delivery Address</th>
                                <th className="border-0 py-2">Product Details</th>
                                <th className="border-0 py-2">Amount</th>
                                <th className="border-0 py-2">Payment</th>
                                <th className="border-0 pe-4 py-2" style={{ minWidth: '160px' }}>Status / Update</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order, idx) => {
                                const isCancelled = order.status === 'Cancelled';
                                const isDelivered = order.status === 'Delivered';
                                return (
                                <tr key={order.id} className="border-bottom last-child-no-border">
                                    <td className="ps-4 py-2 text-muted extra-small fw-bold" style={{ width: '50px' }}>{idx + 1}</td>
                                    <td className="py-2">
                                        <div className="fw-bold text-primary small mb-0">{order.id}</div>
                                        <div className="text-muted extra-small">{order.date}</div>
                                    </td>
                                    <td className="py-2">
                                        <div className="fw-bold text-dark small mb-0">{order.customer}</div>
                                        <div className="text-muted extra-small">{order.email}</div>
                                        <div className="text-muted extra-small">{order.phone}</div>
                                    </td>
                                    <td className="py-2">
                                        <div className="small mb-0">{order.address.fullAddress}</div>
                                        <div className="text-muted extra-small">{order.address.pincode}</div>
                                        {order.address.nearby && <div className="text-muted extra-small fw-semibold">Near: {order.address.nearby}</div>}
                                    </td>
                                    <td className="py-2">
                                        {order.items.map((item, i) => (
                                            <div key={i} className="order-item-block small">
                                                <span className="fw-semibold">{item.name}</span>
                                                <span className="text-muted extra-small"> x{item.qty} ({item.weight})</span>
                                                {item.message && <div className="text-muted extra-small" style={{ fontStyle: 'italic' }}>Msg: "{item.message}"</div>}
                                            </div>
                                        ))}
                                    </td>
                                    <td className="py-2 fw-bold text-dark">{order.total}</td>
                                    <td className="py-2">
                                        <div className="small fw-semibold">{order.payment.amount}</div>
                                        <Badge bg={order.payment.status === 'Refunded' ? 'soft-danger' : 'soft-success'} className={`border-0 x-small fw-bold ${order.payment.status === 'Refunded' ? 'text-danger' : 'text-success'}`}>
                                            {isCancelled ? 'Refunded' : order.payment.status}
                                        </Badge>
                                        {order.payment.id !== '-' && (
                                            <div className="text-muted extra-small mt-1" title={order.payment.id}>ID: {order.payment.id.slice(0, 12)}...</div>
                                        )}
                                    </td>
                                    <td className="py-2 pe-4">
                                        <Form.Select
                                            size="sm"
                                            value={order.status}
                                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                            className={`status-select-admin ${order.status.toLowerCase()}`}
                                            disabled={isDelivered}
                                        >
                                            {statusOptions.map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </Form.Select>
                                        {isCancelled && (
                                            <Badge bg="soft-danger" className="mt-1 border-0 x-small fw-bold text-danger d-inline-flex align-items-center">
                                                <XCircle size={10} className="me-1" /> Cancelled
                                            </Badge>
                                        )}
                                        {isDelivered && (
                                            <Badge bg="soft-success" className="mt-1 border-0 x-small fw-bold text-success d-inline-flex align-items-center">
                                                <CheckCircle size={10} className="me-1" /> Delivered
                                            </Badge>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminOrders;
